package sarka.design.skoria.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.navigation.NavController
import androidx.navigation.NavHostController
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * Wires the browser Back/Forward buttons to the navigation back stack on web.
 *
 * Tracks back-stack *depth* rather than re-navigating by route string on
 * popstate. Re-navigating to a stored route doesn't round-trip for destinations
 * with arguments — `destination.route` is the pattern ("conversation/{withUser}")
 * not the filled value — so instead a browser Back simply pops the stack and a
 * forward navigation pushes one history entry. Depth is opaque, independent of
 * how routes are defined or what arguments they carry.
 */
@Composable
actual fun BrowserHistoryEffect(navController: NavHostController) {
    val state = remember { BrowserHistoryState() }

    DisposableEffect(navController) {
        val navListener = NavController.OnDestinationChangedListener { controller, _, _ ->
            val depth = controller.currentBackStack.value.count { it.destination.route != null }

            if (!state.initialized) {
                state.initialized = true
                state.depth = depth
                window.history.replaceState(depth, "", null)
                return@OnDestinationChangedListener
            }

            // A change we caused by handling a popstate: sync depth, don't push.
            if (state.isHandlingBrowserNav) {
                state.depth = depth
                return@OnDestinationChangedListener
            }

            // Forward navigation from within the app deepens the stack; mirror
            // that with a browser history entry so Back has something to pop.
            if (depth > state.depth) {
                window.history.pushState(depth, "", null)
            }
            state.depth = depth
        }

        val popStateListener: (Event) -> Unit = {
            // Browser Back/Forward: pop the app's stack rather than re-navigating
            // by route string. If nothing is left to pop, let the browser do its
            // normal thing.
            if (state.initialized && navController.previousBackStackEntry != null) {
                state.isHandlingBrowserNav = true
                navController.popBackStack()
                state.isHandlingBrowserNav = false
            }
        }

        navController.addOnDestinationChangedListener(navListener)
        window.addEventListener("popstate", popStateListener)

        onDispose {
            navController.removeOnDestinationChangedListener(navListener)
            window.removeEventListener("popstate", popStateListener)
        }
    }
}

private class BrowserHistoryState {
    var initialized: Boolean = false
    var isHandlingBrowserNav: Boolean = false
    var depth: Int = 0
}
